/**
 * @class
 * @extends Netx.Collections.SearchRules
 */
import Base from './searchRules';
import Model from '../models/searchRule_asset';

import {dispatcher} from '@netx/core-next/instance/dispatcher';

import SearchFacetsCollection from './searchFacets';

import assetCustomConfig from '@netx/core-asset/config';
import base64 from 'core-libs/base64';
import {getPreference} from '@netx/core-user-next/utils/getPreference';
import {escapeURI} from '@netx/core/utils/escapeURI';
import {unescapeURI} from '@netx/core/utils/unescapeURI';

export default class SearchRules_Assets extends Base {
	/** @override */
	constructor(models, opts = {}) {
		opts.model = opts.model || Model;

		super(models, opts);

		/**
		 * Ignore recording stats for our searches
		 * Boolean as number
		 * @type {number}
		 * @default
		 */
		this.ignoreStat = opts.ignoreStat || 0;

		/**
		 * @type {number}
		 * @default
		 */
		this.notifyType = opts.notifyType || 0;

		/**
		 * Unless we want to save the search, leave this field blank.
		 * @type {string}
		 * @default
		 */
		this.saveSearch = opts.saveSearch || '';

		/**
		 * The field to ort by
		 * @type {string}
		 * @default
		 */
		this.sortField = opts.files || 'file';

		/**
		 * Must be set prior to super
		 * @memberOf Netx.Collections.SearchRules_assets
		 * @alias userPrefKeys
		 * @type {object}
		 */
		this.userPrefKeys = _.extend(
			{
				sortField: 'asset.list.sort.field',
				sortOrder: 'asset.list.sort.order',
			},
			opts.userPrefKeys,
		);

		/**
		 * Last asset in query to fetch
		 * @type {number}
		 * @default
		 */
		this.maxItems = assetCustomConfig.CHUNK_SIZE + 1;

		/**
		 * When Netx.Collections.Assets uses this search to perform a faceted search mode,
		 * the returned facets will be attached here in a Netx.Collections.SearchFacets object.
		 * @memberOf Netx.Collections.SearchRules_assets
		 * @alias searchFacets
		 */
		this.searchFacets = opts.searchFacets || new SearchFacetsCollection();
		this.addSubCollection(this.searchFacets);

		// Listen for sort events
		this.listenTo(dispatcher, 'user:sort', this.setSort);
	}

	/** @override */
	createSearchObject(ignoreQuirks) {
		return {
			...super.createSearchObject(ignoreQuirks),
			saveSearch: this.saveSearch,
			notifyType: this.notifyType,
			ignoreStat: this.ignoreStat,
		};
	}

	/**
	 * Intialize the Asset Search Object Properties
	 * @override
	 * @param {object} opts Properties to set
	 */
	initProperties(opts = {}) {
		if (opts.maxItems != null) {
			console.warn(
				'Error: attempting to set maxItems in search rules collection, but it will be ignored. Set pageSize or chunkSize on asset collection instead.',
			);
			delete opts.maxItems;
		}

		super.initProperties(opts);

		_.extend(this, {
			// Unless we want to save the search, leave this field blank.
			saveSearch: opts.saveSearch || this.saveSearch,
			// notifyType
			notifyType: opts.notifyType != null ? opts.notifyType : this.notifyType,
			// Go ahead and record stats for our searches.
			ignoreStat: opts.ignoreStat != null ? opts.ignoreStat : this.ignoreStat,
		});

		return this;
	}

	/**
	 * Convert base64 encoded search string to searchRules
	 * @method
	 * @param {string} str Base64 encoded search string
	 * @return {object} Asset search object
	 */
	strTosALO(str) {
		let small = '';
		try {
			small = JSON.parse(base64.decode(unescapeURI(str)));
		} catch (e) {
			// a bogus URL did not decode into a legit object.
			dispatcher.trigger('app:alert', 'Search not found.', 'Info');
			return undefined;
		}

		const rules = new this.constructor().createSearchObject();
		_.each(this.sALOstrMap, (realName, smallName) => {
			if (small[smallName] == null) return;
			rules[realName] = small[smallName];
		});

		return rules;
	}

	/**
	 * Respond to sort events
	 * @method
	 * @param {string} sortField Field to sort by
	 * @param {number} sortOrder Sort direction ( 0/1 )
	 */
	setSort(sortField, sortOrder) {
		this.sortField = sortField;
		this.sortOrder = sortOrder;
	}

	/**
	 * Convert advanced search rules to a base64 encoded string
	 * @method
	 * @return {string} Base64 encoded search string
	 */
	toStr(useThisSort = false) {
		const searchObj = this.createSearchObject(true);
		const small = {};
		// a new object will have the defaults ...
		const defs = new this.constructor().createSearchObject();

		// NOTE: portal does not use advanced search
		// theme uses it but would always be relying on the user sort
		// this is just a hold over until we can reactify advanced search (creation)
		if (!useThisSort) {
			searchObj.sortField = getPreference('asset.list.sort.field') || 'file';
			searchObj.sortOrder =
				parseInt(getPreference('asset.list.sort.order')) || 0;
		}

		// create a minified object, so the string representation can be smaller.
		_.each(this.sALOstrMap, (realName, smallName) => {
			// don't store either of these two fields, and don't store any default values.
			if (realName == 'startPosition' || realName == 'maxItems') return;
			small[smallName] = searchObj[realName];
		});

		const ret = escapeURI(base64.encode(JSON.stringify(small)));
		if (ret.length > 2000) {
			console.warn(
				'Warning: advanced search encoded to ' +
					ret.length +
					' chars; Internet Explorer may fail if total URL lengh > 2083 chars.',
			);
		}

		return ret;
	}

	/** @type {string} */
	static get __name__() {
		return 'SearchRules_Assets';
	}
}
